import { marker } from "@jsverse/transloco-keys-manager/marker";
import { absoluteUrl } from "src/app/support/navigation";
import { sntTranslate } from "src/app/support/translate";
import { NavigationBarItem } from "../bootstrap-components/navigation-bar/navigation-bar-item";
import { routes } from "src/app/routing/routes";
import { BootstrapService } from "src/app/modules/bootstrap/bootstrap.service";
import { compact, every, flatten, groupBy, some, toPairs } from "lodash-es";
import { UserService } from "src/app/services/user.service";
import { appState } from "src/app/core/state";
import { MODULES } from "src/app/core/modules";
import { RegionService } from "src/app/services/feature-access-control/region.service";
import { WarehouseStorageLight } from "src/app/backend/models/warehouse/warehouse-storage";
import { ConfigurationService } from "src/app/services/configuration.service";
import { FEATURES, FeatureService } from "src/app/services/feature.service";
import { NotifyNewContentNames } from "src/app/components/ui/notify-new-content/notify-new-content.service";
import {
  IntegrationDropPoints,
  IntegrationLayoutMenu
} from "src/app/integrations/integration-layout";
import { IntegrationService } from "src/app/services/integration.service";
import { COLORS_FOR_MODULES } from "src/app/support/colors";

export async function getPmsNavigationBarMenu(providers: {
  featureService: FeatureService;
  regionService: RegionService;
  bootstrapService: BootstrapService;
  userService: UserService;
  configurationService: ConfigurationService;
  integrationService: IntegrationService;
}): Promise<NavigationBarItem[]> {
  const warehouseEnabled = providers.featureService.isEnabled(
    FEATURES.WAREHOUSE
  );

  const marketingEnabled = providers.featureService.isEnabled(
    FEATURES.MARKETING
  );

  const fiscalProfiles = providers.bootstrapService.getFiscalProfiles() || [];

  const fiscalProfilesWithTesseraSanitariaEnabled =
    fiscalProfiles?.filter((x) => x.tesseraSanitaria?.enabled) || [];

  const fiscalProfilesWithFatturazioneElettronicaEnabled =
    fiscalProfiles?.filter((x) => x.electronicInvoice?.enabled) || [];

  const registryItems: NavigationBarItem[] = [
    {
      icon: "fa-users",
      title: sntTranslate(marker("Contatti")),
      url: absoluteUrl("/organization/customers-registry"),
      disabled: providers.userService.unable("read", "customer", {
        tenant: appState.tenantId
      })
    },
    {
      icon: "fa-paw",
      title: sntTranslate(marker("Pazienti")),
      url: absoluteUrl("/organization/patients-registry"),
      disabled: providers.userService.unable("read", "patient", {
        tenant: appState.tenantId
      })
    }
  ];

  const recoveryMenuItems: NavigationBarItem[] = [
    {
      icon: "fa-house-medical-flag",
      title: sntTranslate(marker("Ricoverati")),
      url: routes.organization.recoveries.in_progress(),
      disabled: providers.userService.unable("read", "recovery", {
        tenant: appState.tenantId
      })
    },
    {
      icon: "fa-files-medical",
      title: sntTranslate(marker("Storico")),
      url: routes.organization.recoveries.all(),
      disabled: providers.userService.unable("read", "recovery", {
        tenant: appState.tenantId
      })
    }
  ];

  const deskMenuItems: NavigationBarItem[] = [
    {
      title: sntTranslate(marker("Agenda")),
      url: absoluteUrl("/organization/calendar"),
      icon: "fa-calendar",
      disabled: providers.userService.unable("read", "appointment", {
        tenant: appState.tenantId
      })
    },
    {
      title: sntTranslate(marker("Flowboard")),
      url: absoluteUrl("/organization/board"),
      icon: "fa-square-kanban",
      disabled:
        !providers.featureService.isEnabled(FEATURES.FLOWBOARD) ||
        providers.userService.unable("read", "appointment", {
          tenant: appState.tenantId
        }) ||
        providers.userService.unable("read", "ticket", {
          tenant: appState.tenantId
        })
    },
    {
      title: sntTranslate(marker("Task")),
      url: absoluteUrl("/organization/tasks"),
      icon: "fa-list-check",
      disabled: !providers.userService.able("read", "task", {
        tenant: appState.tenantId
      })
    }
  ];

  const quotesMenuItems: NavigationBarItem[] = [
    {
      title: sntTranslate(marker("Preventivi")),
      icon: "fa-file-invoice-dollar",
      url: absoluteUrl("/organization/quotes"),
      disabled: providers.userService.unable("read", "quote", {
        tenant: appState.tenantId
      })
    }
  ];

  const invoicesMenuItems: NavigationBarItem[] = [
    {
      title: sntTranslate(marker("Notule")),
      icon: "fa-file-pen",
      url: routes.organization.invoices.paper("draft")
    },
    {
      title: sntTranslate(marker("Fatture emesse")),
      icon: "fa-file-invoice-dollar",
      url: routes.organization.invoices.paper("emitted")
    }
  ];

  const electronicInvoiceMenuItems: NavigationBarItem[] = flatten(
    fiscalProfilesWithFatturazioneElettronicaEnabled.map((fiscalProfile) => {
      return [
        {
          title: fiscalProfile.vatNumber || fiscalProfile.name,
          header: true
        },
        {
          icon: "fa-file-invoice-dollar",
          title: sntTranslate(marker("Fatture da inviare")),
          url: routes.organization.sendings.fiscal_profiles.invoices_to_send(
            fiscalProfile.id,
            "fatturazione-elettronica"
          )
        },
        {
          icon: "fa-arrows-up-to-line",
          title: sntTranslate(marker("Invii")),
          url: routes.organization.sendings.fiscal_profiles.sendings(
            fiscalProfile.id,
            "fatturazione-elettronica"
          )
        },
        {
          icon: "fa-file-import",
          title: sntTranslate(marker("Fatture passive")),
          url: routes.organization.passive.fiscal_profiles.passive(
            fiscalProfile.id
          ),
          disabled: !fiscalProfile.electronicInvoice?.passiveInvoiceEnabled
        }
      ];
    })
  );

  const tesseraSanitariaMenuItems: NavigationBarItem[] = flatten(
    fiscalProfilesWithTesseraSanitariaEnabled.map((fiscalProfile) => {
      return [
        {
          title: fiscalProfile.vatNumber || fiscalProfile.name,
          header: true
        },
        {
          icon: "fa-file-invoice-dollar",
          title: sntTranslate(marker("Fatture da inviare")),
          url: routes.organization.sendings.fiscal_profiles.invoices_to_send(
            fiscalProfile.id,
            "tessera-sanitaria"
          )
        },
        {
          icon: "fa-arrows-up-to-line",
          title: sntTranslate(marker("Invii")),
          url: routes.organization.sendings.fiscal_profiles.sendings(
            fiscalProfile.id,
            "tessera-sanitaria"
          )
        }
      ];
    })
  );

  const warehouseMenuItems: NavigationBarItem[] = extendWithIntegrationMenus({
    dropPoint: IntegrationDropPoints.MENU_WAREHOUSE,
    integrationService: providers.integrationService,
    userService: providers.userService,
    items: compact(<NavigationBarItem[] | null>[
      {
        title: sntTranslate(marker("Consegne")),
        url: routes.warehouse.delivery.master(),
        icon: "fa-light fa-truck-ramp-box",
        disabled: providers.userService.unable("read", "movement")
      },
      {
        title: sntTranslate(marker("Vendite")),
        url: routes.warehouse.sellings.master(),
        icon: "fa-light fa-box-dollar",
        disabled: providers.userService.unable("read", "movement")
      },
      {
        title: sntTranslate(marker("Inventario")),
        url: routes.warehouse.goods.master(),
        icon: "fa-light fa-shelves",
        disabled: providers.userService.unable("read", "good")
      },
      {
        title: sntTranslate(marker("Ordini")),
        url: "not-found",
        icon: "fa-light fa-cart-flatbed-boxes",
        disabled: true,
        notImplemented: true
      },
      {
        title: sntTranslate(marker("Storage")),
        header: true
      },
      ...appState.storages.map((storage: WarehouseStorageLight) => {
        return <NavigationBarItem>{
          title: storage.name,
          url: routes.warehouse.storage.id(storage.id),
          icon: "fa-light fa-warehouse-full",
          disabled: providers.userService.unable("read", "storage")
        };
      }),
      appState.storages.length === 0
        ? <NavigationBarItem>{
            title: sntTranslate(marker("Nessun magazzino configurato")),
            disabled: true,
            notImplemented: true
          }
        : null
    ])
  });

  const managerMembersMenuItems: NavigationBarItem[] = [
    {
      icon: "fa-user-friends",
      title: sntTranslate(marker("Utenti")),
      url: absoluteUrl("/configuration/manage-users/members"),
      disabled: providers.userService.unable("read", "member", {
        tenant: appState.tenantId
      })
    },
    {
      icon: "fa-users",
      title: sntTranslate(marker("Gruppi")),
      url: absoluteUrl("/configuration/manage-users/groups"),
      disabled: providers.userService.unable("read", "group", {
        tenant: appState.tenantId
      })
    },
    {
      icon: "fa-key",
      title: sntTranslate(marker("Ruoli")),
      url: absoluteUrl("/configuration/manage-users/roles"),
      disabled: providers.userService.unable("read", "role", {
        tenant: appState.tenantId
      })
    },
    {
      icon: "fa-user-clock",
      title: sntTranslate(marker("Turni")),
      url: routes.configuration.manage_users.shifts.master(),
      disabled: providers.userService.unable("read", "schedule")
    }
  ];

  const marketingMenuItems: NavigationBarItem[] = [
    {
      icon: "fa-magnifying-glass-chart",
      title: sntTranslate(marker("Campagne")),
      url: absoluteUrl("/organization/comunications/campaigns"),
      disabled: providers.userService.unable("read", "campaign")
    },
    {
      icon: "fa-objects-align-left",
      title: sntTranslate(marker("Liste")),
      url: absoluteUrl("/organization/comunications/segmentations"),
      disabled: providers.userService.unable("read", "segmentation")
    }
  ];

  const configurationMenuItems: NavigationBarItem[] = [
    {
      icon: "fa-file-invoice-dollar",
      title: sntTranslate(marker("Profili fiscali")),
      url: absoluteUrl("/organization/fiscal-profiles"),
      disabled: providers.userService.unable("read", "fiscal-profile")
    },
    {
      icon: "fa-booth-curtain",
      title: sntTranslate(marker("Stanze")),
      url: absoluteUrl("/company/wards"),
      disabled: providers.userService.unable("read", "room")
    },
    {
      icon: "fa-tags",
      title: sntTranslate(marker("Tag")),
      url: absoluteUrl("/configuration/tag"),
      disabled: providers.userService.unable("read", "tag")
    },
    {
      icon: "fa-heading",
      title: sntTranslate(marker("Carta intestata")),
      url: absoluteUrl("/configuration/letterhead"),
      disabled: providers.userService.unable("read", "letterhead")
    },
    {
      icon: "fa-building-columns",
      title: sntTranslate(marker("Conti corrente")),
      url: absoluteUrl("/organization/bank-accounts"),
      disabled: providers.userService.unable("read", "bank-account")
    },
    {
      icon: "fa-credit-card",
      title: sntTranslate(marker("Metodi di pagamento")),
      url: absoluteUrl("/organization/payment-methods"),
      disabled: providers.userService.unable("read", "payment-method")
    },
    {
      icon: "fa-percent",
      title: sntTranslate(marker("Aliquote IVA")),
      url: absoluteUrl("/organization/tax-rates"),
      disabled: true,
      notImplemented: true
    },
    {
      icon: "fa-ruler",
      title: sntTranslate(marker("Unita di misura")),
      url: absoluteUrl("/organization/unit-measure"),
      disabled: true,
      notImplemented: true
    },
    {
      icon: "fab fa-rev",
      title: sntTranslate(marker("Centri di ricavo")),
      url: absoluteUrl("/organization/revenue-centers"),
      disabled: true,
      notImplemented: true
    },
    {
      icon: "fa-dollar-sign",
      title: sntTranslate(marker("Centri di costo")),
      url: absoluteUrl("/organization/cost-centers"),
      disabled: true,
      notImplemented: true
    }
  ];

  const analyticsMenuItems: NavigationBarItem[] = [
    {
      icon: "fa-chart-simple",
      title: sntTranslate(marker("Fatturato")),
      url: routes.organization.analytics.sales.master(),
      disabled: providers.userService.unable("read", "analytics")
    },
    {
      icon: "fa-money-bill-trend-up",
      title: sntTranslate(marker("Incasso")),
      url: routes.organization.analytics.incomes.master(),
      disabled: providers.userService.unable("read", "analytics")
    },
    {
      icon: "fa-users",
      title: sntTranslate(marker("Contatti")),
      url: routes.organization.analytics.customers.master(),
      disabled: providers.userService.unable("read", "analytics")
    },
    {
      icon: "fa-paw",
      title: sntTranslate(marker("Pazienti")),
      url: routes.organization.analytics.patients.master(),
      disabled: providers.userService.unable("read", "analytics")
    },
    {
      icon: "fa-house-medical-circle-check",
      title: sntTranslate(marker("Check-in")),
      url: routes.organization.analytics.tickets.master(),
      disabled: providers.userService.unable("read", "analytics")
    }
  ];

  const inventoryMenuItems: NavigationBarItem[] = compact([
    {
      icon: "fa-light fa-hand-holding-dollar",
      title: sntTranslate(marker("Clienti insoluti")),
      url: routes.organization.late_paying_customers.master(),
      disabled: providers.userService.unable("read", "invoice")
    },
    providers.featureService.isEnabled(FEATURES.CUSTOMER_SURVEY)
      ? {
          icon: "fa-light fa-square-poll-vertical",
          title: sntTranslate(marker("Soddisfazione clienti")),
          url: routes.organization.surveys.list(),
          newFeatureName: NotifyNewContentNames.SURVEYS_MASTER,
          disabled: providers.userService.unable("read", "survey", {
            tenant: appState.tenantId
          })
        }
      : null,
    {
      icon: "fa-book-open",
      title: sntTranslate(marker("Conti aperti")),
      url: absoluteUrl("/organization/open-books"),
      disabled: providers.userService.unable("read", "open-book")
    },
    {
      icon: "fa-cash-register",
      title: sntTranslate(marker("Chiusura di cassa")),
      url: absoluteUrl("/organization/cash-closure"),
      disabled: providers.userService.unable("read", "payment")
    },
    {
      icon: "fa-money-check-dollar-pen",
      title: sntTranslate(marker("Tariffario")),
      url: absoluteUrl("/organization/rate-table"),
      disabled: providers.userService.unable("read", "rate-table")
    },
    {
      icon: "fa-pills",
      title: sntTranslate(marker("Farmaci")),
      url: absoluteUrl("/organization/drugs"),
      disabled: providers.userService.unable("read", "drug")
    },
    {
      icon: "fa-list",
      title: sntTranslate(marker("Prodotti")),
      url: routes.organization.products.master(),
      disabled: providers.userService.unable("read", "product")
    },
    {
      icon: "fa-arrow-up-big-small",
      title: sntTranslate(marker("Ordini di lavoro")),
      url: absoluteUrl("/organization/work-orders"),
      disabled: providers.userService.unable("read", "work-order")
    },
    {
      icon: "fa-file-prescription",
      title: sntTranslate(marker("Ricette")),
      url: absoluteUrl("/organization/prescriptions"),
      disabled: providers.userService.unable("read", "prescription")
    },
    {
      icon: "fa-file-export",
      title: sntTranslate(marker("Esportazioni")),
      url: routes.organization.export.list(),
      disabled: providers.userService.unable("read", "export")
    },
    {
      icon: "fa-file-import",
      title: sntTranslate(marker("Importazioni")),
      url: routes.organization.import.list(),
      disabled: providers.userService.unable("read", "import")
    },
    {
      icon: "fa-alarm-clock",
      title: sntTranslate(marker("Promemoria")),
      url: absoluteUrl("/organization/reminders"),
      disabled: providers.userService.unable("read", "reminder")
    },
    {
      icon: "fa-light fa-user-doctor",
      title: sntTranslate(marker("Medici esterni")),
      url: absoluteUrl("/organization/physicians"),
      disabled: providers.userService.unable("read", "physician")
    },
    {
      icon: "fa-light fa-split",
      title: sntTranslate(marker("Rapporti di produzione")),
      url: routes.organization.remunerations.master(),
      disabled: providers.userService.unable("read", "remuneration")
    },
    {
      title: sntTranslate(marker("Magazzino")),
      header: true
    },
    {
      title: sntTranslate(marker("Storage")),
      url: routes.warehouse.storage_registry.master(),
      icon: "fa-light fa-boxes-packing",
      disabled:
        !warehouseEnabled || providers.userService.unable("read", "storage")
    },
    {
      title: sntTranslate(marker("Unità organizzative")),
      url: routes.warehouse.business_unit_registry.master(),
      icon: "fa-light fa-box-dollar",
      disabled:
        !warehouseEnabled ||
        providers.userService.unable("read", "business-unit")
    },
    {
      icon: "fa-light fa-truck-field",
      title: sntTranslate(marker("Fornitori")),
      url: routes.warehouse.supplier_registry.master(),
      disabled:
        !warehouseEnabled || providers.userService.unable("read", "supplier")
    },
    {
      title: sntTranslate(marker("Modelli")),
      header: true
    },
    {
      title: sntTranslate(marker("Preventivi")),
      icon: "fa-file-contract",
      url: absoluteUrl("/organization/quote-templates"),
      disabled: providers.userService.unable("create", "template")
    },
    {
      icon: "fa-files",
      title: sntTranslate(marker("Documenti")),
      url: absoluteUrl("/organization/documents"),
      disabled: providers.userService.unable("create", "template")
    },
    {
      icon: "fa-message",
      title: sntTranslate(marker("Promemoria")),
      url: routes.organization.reminders_templates.master(),
      disabled: providers.userService.unable("create", "template")
    },
    {
      icon: "fa-files-medical",
      title: sntTranslate(marker("Schede")),
      url: routes.organization.soap_templates.list(),
      disabled: providers.userService.unable("create", "template")
    },
    {
      icon: "fa-calendars",
      title: sntTranslate(marker("Appuntamenti")),
      url: absoluteUrl("/organization/appointment-templates"),
      disabled: providers.userService.unable("create", "template")
    },
    {
      icon: "fa-vial-circle-check",
      title: sntTranslate(marker("Vaccini")),
      url: routes.organization.vaccines.list(),
      disabled: providers.userService.unable("create", "template")
    },
    {
      icon: "fa-magnifying-glass-chart",
      title: sntTranslate(marker("Comunicazioni")),
      url: routes.organization.campaign_templates.master()
    }
  ]);

  return compact([
    {
      title: sntTranslate(marker("Dashboard")),
      url: absoluteUrl("/organization/dashboard"),
      icon: "fa-house",
      disabled: false,
      color: COLORS_FOR_MODULES.DASHBOARD
    },
    {
      title: sntTranslate(marker("Desk")),
      icon: "fa-desktop",
      disabled: every(
        deskMenuItems.filter((x) => !x.notImplemented),
        (x) => x.disabled
      ),
      items: deskMenuItems,
      color: COLORS_FOR_MODULES.DESK
    },
    {
      title: sntTranslate(marker("Anagrafica")),
      icon: "fa-address-book",
      url: absoluteUrl("/organization"),
      disabled: some(
        registryItems.filter((x) => !x.notImplemented),
        (x) => x.disabled
      ),
      items: registryItems,
      color: COLORS_FOR_MODULES.REGISTRY
    },
    {
      title: sntTranslate(marker("Degenza")),
      icon: "fa-hospitals",
      disabled: some(
        recoveryMenuItems.filter((x) => !x.notImplemented),
        (x) => x.disabled
      ),
      items: recoveryMenuItems,
      color: COLORS_FOR_MODULES.RECOVERY
    },
    {
      title: sntTranslate(marker("Preventivi")),
      icon: "fa-file-signature",
      url: absoluteUrl("/organization/quotes"),
      disabled: some(
        quotesMenuItems.filter((x) => !x.notImplemented),
        (x) => x.disabled
      ),
      items: quotesMenuItems,
      color: COLORS_FOR_MODULES.QUOTES
    },
    {
      title: sntTranslate(marker("Fatture")),
      icon: "fa-file-invoice-dollar",
      url: "",
      disabled: providers.userService.unable("read", "invoice", {
        tenant: appState.tenantId
      }),
      items: invoicesMenuItems,
      color: COLORS_FOR_MODULES.INVOICES
    },
    providers.regionService.canBeActivatedSync(MODULES.FATTURAZIONE_ELETTRONICA)
      ? {
          title: sntTranslate(marker("Fatture elettroniche")),
          icon: "fa-file-code",
          disabled:
            !providers.featureService.isEnabled(
              FEATURES.FATTURAZIONE_ELETTRONICA
            ) ||
            providers.userService.unable("read", "invoice", {
              tenant: appState.tenantId
            }) ||
            fiscalProfilesWithFatturazioneElettronicaEnabled.length === 0,
          url: "",
          items: electronicInvoiceMenuItems,
          color: COLORS_FOR_MODULES.ELECTRONIC_INVOICING
        }
      : null,
    providers.regionService.canBeActivatedSync(MODULES.TESSERA_SANITARIA)
      ? {
          title: sntTranslate(marker("Tessera sanitaria")),
          icon: "fa-file-medical",
          disabled:
            !providers.featureService.isEnabled(FEATURES.TESSERA_SANITARIA) ||
            providers.userService.unable("read", "invoice", {
              tenant: appState.tenantId
            }) ||
            fiscalProfilesWithTesseraSanitariaEnabled.length === 0,
          url: absoluteUrl("/organization/tessera-sanitaria/sending"),
          items: tesseraSanitariaMenuItems,
          color: COLORS_FOR_MODULES.TESSERA_SANITARIA
        }
      : null,
    {
      title: sntTranslate(marker("Magazzino")),
      icon: "fa-warehouse",
      url: routes.warehouse.goods.master(),
      disabled:
        !warehouseEnabled ||
        every(
          warehouseMenuItems
            .filter((x) => !x.notImplemented)
            .filter((x) => !x.header),
          (item) => item.disabled
        ),
      items: warehouseMenuItems,
      color: COLORS_FOR_MODULES.WAREHOUSE
    },
    {
      title: sntTranslate(marker("Inventario")),
      icon: "fa-boxes",
      url: absoluteUrl("/inventory"),
      disabled: every(
        inventoryMenuItems
          .filter((x) => !x.notImplemented)
          .filter((x) => !x.header),
        (x) => x.disabled
      ),
      items: inventoryMenuItems,
      color: COLORS_FOR_MODULES.INVENTORY
    },
    {
      icon: "fa-chart-mixed",
      title: sntTranslate(marker("Statistiche")),
      url: absoluteUrl("/organization/analytics"),
      disabled: some(
        analyticsMenuItems.filter((x) => !x.notImplemented),
        (x) => x.disabled
      ),
      items: analyticsMenuItems,
      color: COLORS_FOR_MODULES.STATISTICS
    },
    {
      title: sntTranslate(marker("KPI")),
      url: absoluteUrl("/organization/kpi"),
      icon: "fa-chart-line-up",
      disabled: false,
      beta: true,
      color: COLORS_FOR_MODULES.KPI
      //TODO: gestire permessi del modulo,
    },
    {
      icon: "fa-siren-on",
      title: sntTranslate(marker("Allarmi")),
      url: absoluteUrl("/organization/alarms"),
      disabled: false,
      color: COLORS_FOR_MODULES.ALARMS
    },
    {
      icon: "fa-filter",
      title: sntTranslate(marker("Ricerche avanzate")),
      url: absoluteUrl("/organization/filters"),
      disabled: false,
      color: COLORS_FOR_MODULES.FILTERS,
      beta: true
    },
    {
      icon: "fa-megaphone",
      title: sntTranslate(marker("Comunicazioni")),
      url: absoluteUrl("/organization/comunications"),
      disabled: !marketingEnabled,
      items: marketingMenuItems,
      color: COLORS_FOR_MODULES.MARKETING
    },
    {
      icon: "fa-puzzle",
      title: sntTranslate(marker("Marketplace")),
      url: routes.organization.integrations.master(),
      disabled: providers.userService.unable("read", "integration", {
        tenant: appState.tenantId
      }),
      color: COLORS_FOR_MODULES.MARKETPLACE
    },
    {
      icon: "fa-users-cog",
      title: sntTranslate(marker("Gestione utenti")),
      disabled: every(
        managerMembersMenuItems.filter((x) => !x.notImplemented),
        (x) => x.disabled
      ),
      items: managerMembersMenuItems,
      color: COLORS_FOR_MODULES.MANAGE_USERS
    },
    {
      title: sntTranslate(marker("Configurazioni")),
      icon: "fa-cog",
      url: absoluteUrl("/company"),
      disabled: every(
        configurationMenuItems.filter((x) => !x.notImplemented),
        (x) => x.disabled
      ),
      items: configurationMenuItems,
      color: COLORS_FOR_MODULES.COMPANY
    },
    {
      icon: "fa-circle-question",
      title: sntTranslate(marker("Centro assistenza")),
      url: routes.organization.help_center.master(),
      disabled: false,
      color: COLORS_FOR_MODULES.DEFAULT
    }
  ]);
}

function extendWithIntegrationMenus({
  items,
  dropPoint,
  integrationService,
  userService
}: {
  items: NavigationBarItem[];
  dropPoint: IntegrationDropPoints;
  integrationService: IntegrationService;
  userService: UserService;
}): NavigationBarItem[] {
  if (userService.unable("read", "integration")) {
    return items;
  }

  const menus = integrationService.getMenusByDropPoint(dropPoint);

  const menusByIntegration = groupBy(
    menus,
    (x) => x.installation!.integrationId
  );

  return [
    ...items,
    ...flatten(
      toPairs(menusByIntegration).map(([integrationId, menus]) => {
        const integration = integrationService.getById(integrationId);

        return [
          <NavigationBarItem>{
            title: integration.name,
            header: true
          },
          ...menus.map((menu: IntegrationLayoutMenu) => {
            return <NavigationBarItem>{
              title: menu.label,
              url: menu.path,
              icon: menu.icon
            };
          })
        ];
      })
    )
  ];
}
